/**
 * sl-w252: Extract PDF metadata + first-page thumbnail using the existing
 * PDFRenderer (PDFium WASM worker). Returns page count and a data-URL
 * thumbnail of the first page for chat history display.
 *
 * Uses the same @toeverything/pdf-viewer infrastructure as the attachment viewer.
 */
import { PDFRenderer } from '../../../modules/pdf';

const THUMBNAIL_MAX_WIDTH = 180;
const THUMBNAIL_MAX_HEIGHT = 264;

export interface PdfInfo {
  pageCount: number;
  thumbnailDataUrl: string | null;
}

/**
 * Open a PDF file, count pages, and render a thumbnail of the first page.
 * The PDFRenderer worker is created, used, and destroyed within this call.
 */
export async function extractPdfInfo(file: File): Promise<PdfInfo> {
  const renderer = new PDFRenderer();
  try {
    const buffer = await file.arrayBuffer();
    const meta = await renderer.call('open', { data: buffer });

    // Render first page thumbnail
    let thumbnailDataUrl: string | null = null;
    if (meta.pageSizes.length > 0) {
      const firstPage = meta.pageSizes[0];
      const scale = Math.min(
        THUMBNAIL_MAX_WIDTH / firstPage.width,
        THUMBNAIL_MAX_HEIGHT / firstPage.height,
        1 // Never upscale
      );
      const width = Math.floor(firstPage.width * scale);
      const height = Math.floor(firstPage.height * scale);

      try {
        const { bitmap } = await renderer.call('render', {
          pageNum: 0,
          width,
          height,
        });

        // Convert ImageBitmap to data URL via canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(bitmap, 0, 0);
          thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        }
        bitmap.close();
      } catch {
        // Rendering may fail for some PDFs — still return page count
      }
    }

    return { pageCount: meta.pageCount, thumbnailDataUrl };
  } finally {
    renderer.destroy();
  }
}
